import React, { useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Image from "../../../components/AppImage";
import ImageSourceDropdown from "./ImageSourceDropdown";
import { processImagesWithOverlay } from "../../../utils/imageOverlay";

const SitePhotoCapture = ({
  roofPhotos,
  meterPhotos,
  lineDiagramPhotos,
  overlayMeta,
  capturedLocation,
  onRoofPhotosChange,
  onMeterPhotosChange,
  onLineDiagramPhotosChange,
  errors,
}) => {
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [processingCategory, setProcessingCategory] = useState(null);

  const handleFileSelect = async (files, category, onChange, maxCount = 5) => {
    if (!files?.length) return;
    setProcessingCategory(category);
    try {
      const processedFiles = await processImagesWithOverlay(files, overlayMeta, capturedLocation);
      const currentPhotos =
        category === "roof"
          ? roofPhotos
          : category === "meter"
            ? meterPhotos
            : category === "line_diagram"
              ? lineDiagramPhotos
              : [];
      const newPhotos = [...currentPhotos, ...processedFiles].slice(0, maxCount);
      onChange(newPhotos);
    } finally {
      setProcessingCategory(null);
    }
  };

  const removePhoto = (index, category, currentPhotos, onChange) => {
    const newPhotos = currentPhotos?.filter((_, i) => i !== index);
    onChange(newPhotos);
  };

  const PhotoCategorySection = ({
    title,
    description,
    photos,
    onChange,
    category,
    icon,
    error,
    maxPhotos = 5,
  }) => {
    const inputId = `photo-upload-${category}`;
    const isProcessing = processingCategory === category;

    return (
      <div className="space-y-4 p-4 rounded-lg border border-border bg-muted/30">
        <div className="flex items-center gap-3 mb-3">
          <Icon name={icon} size={20} className="text-primary" />
          <div>
            <h3 className="text-base font-semibold text-foreground">{title}</h3>
            <p className="text-xs text-muted-foreground">{description}</p>
          </div>
        </div>

        <div className="flex items-center justify-between mb-2">
          <p className="text-sm text-muted-foreground">
            Maximum {maxPhotos} photos
          </p>
          <span className="text-sm font-medium text-foreground">
            {photos?.length || 0}/{maxPhotos}
          </span>
        </div>

        {photos?.length < maxPhotos && (
          <ImageSourceDropdown
            idPrefix={inputId}
            triggerLabel={`Add ${title} Photos`}
            triggerSubLabel="Upload or capture"
            multiple
            processing={isProcessing}
            disabled={photos?.length >= maxPhotos}
            onFileSelect={(files) =>
              handleFileSelect(files, category, onChange, maxPhotos)
            }
          />
        )}

        {photos?.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {photos?.map((photo, index) => {
              const photoUrl =
                photo instanceof File ? URL.createObjectURL(photo) : photo;

              return (
                <div
                  key={index}
                  className="relative group rounded-lg overflow-hidden border border-border bg-muted"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <Image
                      src={photoUrl}
                      alt={`${title} photo ${index + 1}`}
                      className="w-full h-full object-contain bg-muted"
                    />
                  </div>

                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      iconName="Eye"
                      onClick={() => {
                        setSelectedPhoto(photo);
                        setSelectedCategory(title);
                      }}
                    >
                      View
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      iconName="Trash2"
                      onClick={() =>
                        removePhoto(index, category, photos, onChange)
                      }
                    >
                      Remove
                    </Button>
                  </div>

                  <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                    {index + 1}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {error && <p className="text-sm text-error">{error}</p>}
      </div>
    );
  };

  return (
    <div className="bg-card rounded-lg border border-border p-4 md:p-6 lg:p-8">
      <div className="flex items-center gap-3 mb-4 md:mb-6">
        <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
          <span className="text-lg font-semibold text-accent">3</span>
        </div>
        <h2 className="text-xl md:text-2xl font-semibold text-foreground">
          Site Photos
        </h2>
      </div>

      <div className="space-y-6">
        <PhotoCategorySection
          title="House Front View"
          description="Photo of the front view of the house"
          photos={roofPhotos}
          onChange={onRoofPhotosChange}
          category="roof"
          icon="Home"
          error={errors?.roofPhotos}
          maxPhotos={1}
        />

        <PhotoCategorySection
          title="Meter"
          description="Photos of the electricity meter"
          photos={meterPhotos}
          onChange={onMeterPhotosChange}
          category="meter"
          icon="Gauge"
          error={errors?.meterPhotos}
        />

        <PhotoCategorySection
          title="Line Diagram"
          description="Single line diagram / layout sketch photo"
          photos={lineDiagramPhotos}
          onChange={onLineDiagramPhotosChange}
          category="line_diagram"
          icon="PenTool"
        />

      </div>

      {errors?.photos && (
        <p className="text-sm text-error mt-4">{errors?.photos}</p>
      )}

      {selectedPhoto && (
        <div
          className="fixed inset-0 bg-black/80 z-[2000] flex items-center justify-center p-4"
          onClick={() => {
            setSelectedPhoto(null);
            setSelectedCategory(null);
          }}
        >
          <div className="relative max-w-4xl w-full">
            <Button
              variant="secondary"
              size="icon"
              iconName="X"
              onClick={() => {
                setSelectedPhoto(null);
                setSelectedCategory(null);
              }}
              className="absolute -top-12 right-0"
            />
            <div className="mb-2 text-white text-center">
              <p className="text-sm font-medium">{selectedCategory}</p>
            </div>
            <Image
              src={
                selectedPhoto instanceof File
                  ? URL.createObjectURL(selectedPhoto)
                  : selectedPhoto
              }
              alt={`Full size ${selectedCategory} photo`}
              className="w-full h-auto rounded-lg"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SitePhotoCapture;
